import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck, AlertCircle, CheckCircle2, Loader2, Camera } from "lucide-react";

/**
 * KYCCard — Streamer verification (Stripe Identity).
 * In preview mode (no real Stripe Identity enabled), submits to admin manual-review queue.
 */
export default function KYCCard() {
  const { user, refreshMe } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [previewMode, setPreviewMode] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/kyc/status");
      setStatus(r.data);
    } catch { /* */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  if (!user) return null;

  const startKyc = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/kyc/start");
      if (r.data.preview_mode) {
        setPreviewMode(true);
        setMsg(r.data.message);
        await load();
        await refreshMe();
        return;
      }
      // real Stripe Identity — redirect to hosted URL or open modal
      if (r.data.verification_url) {
        window.location.href = r.data.verification_url;
      } else {
        setMsg("Verification session created. Refresh in a moment.");
        await load();
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to start verification.");
    } finally { setBusy(false); }
  };

  const st = status?.status || user.kyc_status || "not_started";
  const isVerified = status?.is_verified_streamer || user.is_verified_streamer;

  return (
    <div className="sc-card p-6 space-y-4" data-testid="kyc-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" style={{ color: isVerified ? "var(--sc-volt)" : "var(--sc-pink)" }} />
        <div className="sc-heading text-xl font-bold">Streamer Verification</div>
        {isVerified && <span className="ml-auto text-[10px] sc-mono tracking-widest uppercase px-2 py-1 rounded-sm font-black" style={{ background: "var(--sc-volt)", color: "#0A0A0C" }}>VERIFIED</span>}
      </div>

      {!isVerified && (
        <p className="text-xs text-zinc-400">
          To go live, receive tips, or withdraw earnings, you must complete a one-time identity + age check (18+ required).
          MystiCast uses <span className="text-white font-semibold">Stripe Identity</span> — a bank-grade, GDPR-safe KYC provider that only shares
          your verified age + name flag with us. We never see your document.
        </p>
      )}

      {isVerified ? (
        <div className="flex items-start gap-2 p-3 border border-[color:var(--sc-volt)]/30 bg-[color:var(--sc-volt)]/5 rounded-sm">
          <CheckCircle2 className="w-4 h-4 text-[color:var(--sc-volt)] mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-[color:var(--sc-volt)]">You are a verified streamer</div>
            <div className="text-[11px] text-zinc-400 mt-1">You can go live, receive tips, and cash out.</div>
          </div>
        </div>
      ) : st === "pending" ? (
        <div className="flex items-start gap-2 p-3 border border-white/10 rounded-sm">
          <Loader2 className="w-4 h-4 animate-spin text-[color:var(--sc-cyan)] mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Verification is being reviewed</div>
            <div className="text-[11px] text-zinc-400 mt-1">
              {previewMode ? "Preview mode: an admin will review your request manually." : "Stripe is processing your document. This usually takes under a minute."}
            </div>
          </div>
        </div>
      ) : st === "failed_underage" ? (
        <div className="flex items-start gap-2 p-3 border border-[color:var(--sc-pink)]/30 bg-[color:var(--sc-pink)]/5 rounded-sm">
          <AlertCircle className="w-4 h-4 text-[color:var(--sc-pink)] mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-[color:var(--sc-pink)]">Age requirement not met</div>
            <div className="text-[11px] text-zinc-400 mt-1">You must be at least 18 years old to become a streamer on MystiCast.</div>
          </div>
        </div>
      ) : (
        <div>
          <ul className="space-y-1 mb-4 text-[11px] text-zinc-400">
            <li>• Government-issued photo ID (drivers license, passport, or ID card)</li>
            <li>• A quick selfie (matched against the ID photo)</li>
            <li>• Under 60 seconds on desktop or mobile</li>
          </ul>
          <button
            data-testid="kyc-start-btn"
            onClick={startKyc}
            disabled={busy}
            className="sc-btn w-full flex items-center justify-center gap-2"
          >
            <Camera className="w-4 h-4" /> {busy ? "Starting…" : (st === "failed" || st === "canceled" ? "Retry Verification" : "Verify Identity")}
          </button>
        </div>
      )}

      {msg && <div className="text-[11px] text-zinc-400 sc-mono border-t border-white/5 pt-3" data-testid="kyc-msg">{msg}</div>}
      {status?.message && !msg && st !== "not_started" && (
        <div className="text-[11px] text-zinc-500 sc-mono">{status.message}</div>
      )}
    </div>
  );
}
